// Route-config factory for the Circle / Network / Space admin index routes.
//
// Each kind's admin index route shares the same shape:
//   - `validateSearch` parses `?section=…&status=…&cursor=…`
//   - the component reads params + search, asserts session, wraps the
//     section body in `GroupAdminShell`
//
// The kind-specific bit is which sections exist and which component
// renders each section — that stays at the call site as a `renderSection`
// callback.

import type { ReactNode } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useAuthedSession } from '@/lib/use-session';
import { GroupAdminShell } from '@/components/group/admin/GroupAdminShell';
import {
  resolveGroupAdminSection,
  type GroupAdminKind,
  type GroupAdminSection,
} from '@/components/group/admin/sections';
import {
  resolveBroadcastListStatus,
  type BroadcastListStatus,
} from '@/lib/queries/broadcasts';

export type GroupAdminSearch = {
  section?: GroupAdminSection;
  status?: BroadcastListStatus;
  cursor?: string;
};

export type AuthedSession = NonNullable<ReturnType<typeof useAuthedSession>>;

export type GroupAdminRenderArgs = {
  readonly id: string;
  readonly section: GroupAdminSection;
  readonly broadcastStatus: BroadcastListStatus;
  readonly broadcastCursor: string | undefined;
  readonly session: AuthedSession;
};

export type CreateGroupAdminRouteOpts = {
  readonly kind: GroupAdminKind;
  /** Route id matching the route file, e.g. `/_authed/circles/$id/admin/`. */
  readonly fromId: string;
  readonly renderSection: (args: GroupAdminRenderArgs) => ReactNode;
};

export function createGroupAdminRoute(opts: CreateGroupAdminRouteOpts) {
  const { kind, fromId, renderSection } = opts;

  const validateSearch = (raw: Record<string, unknown>): GroupAdminSearch => {
    const status = resolveBroadcastListStatus(raw.status);
    const cursor =
      typeof raw.cursor === 'string' && raw.cursor.length > 0
        ? raw.cursor
        : undefined;
    return {
      section: resolveGroupAdminSection(kind, raw.section),
      ...(status !== undefined ? { status } : {}),
      ...(cursor !== undefined ? { cursor } : {}),
    };
  };

  function GroupAdminRouteComponent() {
    // `getRouteApi(fromId)` re-resolves the typed Route bound to the file
    // route so `useParams()` / `useSearch()` carry the correct shape.
    const routeApi = getRouteApi(fromId as never);
    const { id } = routeApi.useParams() as { id: string };
    const search = routeApi.useSearch() as GroupAdminSearch;
    const section = search.section ?? 'overview';
    const broadcastStatus = search.status ?? 'all';
    const broadcastCursor = search.cursor;
    const session = useAuthedSession();

    if (session === null) return null;

    return (
      <GroupAdminShell id={id} kind={kind} section={section}>
        {renderSection({
          id,
          section,
          broadcastStatus,
          broadcastCursor,
          session,
        })}
      </GroupAdminShell>
    );
  }

  return {
    validateSearch,
    component: GroupAdminRouteComponent,
  };
}
